export function resolveIncomingCallsPaused(response) {
    const status = typeof response.status === 'string' ? response.status.trim().toUpperCase() : response.status;
    if (status === 'OUTGOING_ONLY')
        return true;
    if (response.status === null)
        return false;
    return coercePhonePauseStatusPaused(response.paused);
}
export function coercePhonePauseStatusPaused(value) {
    if (value === true || value === 1)
        return true;
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        return v === 'true' || v === '1' || v === 'yes';
    }
    return false;
}
/** Default ACW window when estimating elapsed time from `phonePauseEnd` only (override via env). */
export const DEFAULT_PHONE_PAUSE_MS = 2 * 60 * 1000;
function resolvePhoneAcwDurationMs() {
    const raw = typeof process !== 'undefined' && process.env?.PHONE_ACW_DURATION_SECONDS
        ? Number(process.env.PHONE_ACW_DURATION_SECONDS)
        : NaN;
    if (Number.isFinite(raw) && raw > 0)
        return raw * 1000;
    return DEFAULT_PHONE_PAUSE_MS;
}
/** CRM / PBX may return boolean or string for `isPhonePause`. */
export function coercePhonePauseFlag(value) {
    if (value === true || value === 1)
        return true;
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        return v === 'true' || v === '1' || v === 'yes';
    }
    return false;
}
export function parsePhonePauseEndMs(iso) {
    if (!iso?.trim())
        return null;
    const trimmed = iso.trim();
    /** Backend often sends local ISO without `Z` — treat as local wall time. */
    const ms = new Date(trimmed).getTime();
    return Number.isNaN(ms) ? null : ms;
}
/** Countdown from a known pause end timestamp (profile `phonePauseEnd`). */
export function computePhonePauseTimersFromEndsAt(endsAtMs, nowMs = Date.now()) {
    if (endsAtMs == null || Number.isNaN(endsAtMs)) {
        return { isPaused: false, remainingSeconds: 0, elapsedSeconds: 0, endsAtMs: null };
    }
    const remainingSeconds = Math.max(0, Math.ceil((endsAtMs - nowMs) / 1000));
    const isPaused = remainingSeconds > 0;
    const defaultDurationSec = resolvePhoneAcwDurationMs() / 1000;
    const elapsedSeconds = isPaused
        ? Math.max(0, Math.min(defaultDurationSec, defaultDurationSec - remainingSeconds))
        : 0;
    return { isPaused, remainingSeconds, elapsedSeconds, endsAtMs };
}
/**
 * Elapsed ACW seconds from an anchor captured when pause was first observed
 * (accurate after hangup; on hard refresh elapsed restarts from profile load).
 */
export function computeAcwElapsedFromAnchor(initialRemainingSec, currentRemainingSec) {
    return Math.max(0, initialRemainingSec - currentRemainingSec);
}
/** Derive ACW state from profile (`isPhonePause`, `phonePauseEnd`) — login and hard refresh. */
export function computePhonePauseTimers(profile, nowMs = Date.now()) {
    if (!coercePhonePauseFlag(profile?.isPhonePause)) {
        return { isPaused: false, remainingSeconds: 0, elapsedSeconds: 0, endsAtMs: null };
    }
    const endsAtMs = parsePhonePauseEndMs(profile?.phonePauseEnd);
    if (endsAtMs == null) {
        return { isPaused: false, remainingSeconds: 0, elapsedSeconds: 0, endsAtMs: null };
    }
    return computePhonePauseTimersFromEndsAt(endsAtMs, nowMs);
}
/** True when profile reports an active ACW window (`isPhonePause` + future `phonePauseEnd`). */
export function isProfileAcwActive(profile, nowMs = Date.now()) {
    return computePhonePauseTimers(profile, nowMs).isPaused;
}
/**
 * Normalize pause flags from a profile API payload before storing in Redux.
 * Clears stale `isPhonePause` when `phonePauseEnd` is missing or already passed.
 */
export function resolveProfilePhonePauseFields(profile, nowMs = Date.now()) {
    if (!isProfileAcwActive(profile, nowMs)) {
        return { isPhonePause: false, phonePauseEnd: undefined };
    }
    return {
        isPhonePause: true,
        phonePauseEnd: profile.phonePauseEnd?.trim() || undefined,
    };
}
/** Merge pause fields onto a profile record (full GET /api/users/profile responses). */
export function withResolvedProfilePhonePause(profile, nowMs = Date.now()) {
    return {
        ...profile,
        ...resolveProfilePhonePauseFields(profile, nowMs),
    };
}
